/**
 * Light wrapper around navigator.sendBeacon()
 */
let _isSupported = null;

export default class Beacon {

  /**
   * Send the specified object as JSON using Beacon API.
   *
   * See:
   * - https://developer.mozilla.org/en-US/docs/Web/API/Beacon_API
   * - https://w3c.github.io/beacon/#dom-navigator-sendbeacon
   *
   * Returns a boolean to indicate that the data was successfully queued.
   *
   * If we've exceeded our quota for sendBeacon on this page, this method
   * will return false.
   *
   * See: https://www.w3.org/TR/beacon/#sec-sendBeacon-method
   *
   * @param url
   * @param object
   * @returns {boolean}
   */
  static sendAsJson(url, object) {
    try {
      const blob = new Blob([JSON.stringify(object)], {type: 'application/json'});
      return navigator.sendBeacon(url, blob);
    } catch (err) {
      return false; // If Chrome complains about CORS preflights for beacon
    }
  }

  /**
   * Send the specified object as JSON using Beacon API.
   *
   * See:
   * - https://developer.mozilla.org/en-US/docs/Web/API/Beacon_API
   * - https://w3c.github.io/beacon/#dom-navigator-sendbeacon
   *
   * Returns a boolean to indicate that the data was successfully queued.
   *
   * If we've exceeded our quota for sendBeacon on this page, this method
   * will return false.
   *
   * See: https://www.w3.org/TR/beacon/#sec-sendBeacon-method
   *
   * @param url
   * @param object
   * @returns {boolean}
   */
  static sendAsJsonText(url, object) {
    /**
     * HACK: We're sending text/plain for 2 reasons:
     * - we avoid a pre-flight request
     * - workaround to an issue introduced in Chrome 59 ( http://crbug.com/490015 )
     */
    try {
      const blob = new Blob([JSON.stringify(object)], {type: 'text/plain'});
      return navigator.sendBeacon(url, blob);
    } catch (err) {
      return false; // If Chrome complains about CORS preflights for beacon
    }
  }

  /**
   * Returns a boolean to indicate whether Beacon is supported.
   */
  static isSupported() {
    if (typeof _isSupported !== 'boolean') {
      _isSupported = this.isSendBeaconSupported() && this.isBlobSupported();
    }
    return _isSupported;
  }

  /**
   * Returns a boolean to indicate whether navigator.sendBeacon is available.
   */
  static isSendBeaconSupported() {
    // Modified from: https://github.com/Modernizr/Modernizr/blob/master/feature-detects/network/beacon.js
    return 'sendBeacon' in navigator && typeof navigator.sendBeacon === 'function';
  }

  /**
   * Returns a boolean to indicate whether Blob is available.
   */
  static isBlobSupported() {
    // Source: https://github.com/Modernizr/Modernizr/blob/master/feature-detects/blob.js
    try {
      return !!new Blob();
    } catch (e) {
      return false;
    }
  }
}


// WEBPACK FOOTER //
// ./~/@atlassiansox/experiment-common-core/lib/beacon.js